import { CheckboxCustomEvent, IonDatetime, IonDatetimeButton, IonModal, IonSpinner } from '@ionic/react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { Event, FullPost, getNow } from 'model';
import React, { useCallback } from 'react';
import { useAddPostImageMutation, useUpdateDisplayCommentsMutation } from '../api/PostApi';
import { FormHookReturnType, FormValuesType } from '../hooks/useFormValues';
import { useImageUpload } from '../hooks/useImageUpload';
import { usePasteURL } from '../hooks/usePasteURL';
import { filterInlineImages } from '../util/filterInlineImages';
import { BigImageContainer } from './BigImageContainer';
import { Checkbox } from './Forms/Checkbox';
import { FormErrors } from './Forms/FormErrors';
import { FormInput } from './Forms/FormInput';
import { FormTextarea } from './Forms/FormTextarea';
import { TopicSearchBox } from './Forms/TopicSearchBox';
import { IconButton } from './IconButton';
import { LinkPreviewComponent } from './LinkPreviewComponent';
import { IconAdd, IconClose } from './QuirkIcons';
import { UploadImageButton } from './UploadImageButton';
// Styles
import styles from './EventEditFields.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import postEditStyles from './PostEditFields.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import formStyles from './Forms/FormFields.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import postStyles from '../styles/posts.module.css';
import { FormInlineImageArea } from './Forms/FormInlineImageArea';

export const EventEditFields: React.FC<{
	event: FullPost;
	setEvent: (value: FullPost | null | undefined) => void;
	setValues: FormHookReturnType<Event>['setValues'];
	formValues: FormValuesType<Event>;
}> = ({ event, setEvent, setValues, formValues }) => {
	const [updateDisplayComments] = useUpdateDisplayCommentsMutation();

	const { uploadImage } = useImageUpload(event, useAddPostImageMutation);
	const { onPaste: onPasteSocial, loading: loadingSocial } = usePasteURL<Event>(setValues, 'contents');
	const eventDate = formValues.eventDate?.value;
	const eventEndDate = formValues.eventEndDate?.value;

	const isDateAfter = (date1: string | undefined, date2: string | undefined) => {
		if (!date1 || !date2) return false;
		const parsedDate1 = DateTime.fromISO(date1);
		const parsedDate2 = DateTime.fromISO(date2);
		return parsedDate1 > parsedDate2;
	};

	const onPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
		onPasteSocial(e);
		uploadImage(e);
	};

	const handleCommentCheck = useCallback(
		async (e: CheckboxCustomEvent) => {
			setValues({ displayComments: e.target.checked });
			setEvent({ ...event, displayComments: e.target.checked });
			await updateDisplayComments({ postid: event._id, display: e.target.checked });
		},
		[event, setEvent, setValues, updateDisplayComments]
	);

	const handleAddEndDate = () => {
		setValues({
			eventEndDate: DateTime.fromISO(eventDate || DateTime.now().toISO())
				.plus({ hours: 1 })
				.toISO()
		});
	};

	const handleRemoveEndDate = () => {
		setValues({ eventEndDate: '' });
	};

	return (
		<>
			<div className={formStyles.fieldsContainer}>
				{formValues && (
					<>
						{formValues.eventDate?.value && (
							<div
								className={formStyles.fieldContainer}
								style={{ marginBottom: formValues.eventEndDate?.value ? '30px' : '10px' }}
							>
								<div className={formStyles.label} key={eventDate}>
									{eventEndDate === '' ? 'Event Date' : 'Event Start Date'}
								</div>
								<IonDatetimeButton
									data-testid="selectEventStartDate"
									placeholder="Select a start date"
									datetime="datetime"
									className={classNames(formStyles.input, styles.dateButtonContainer)}
								/>

								<IonModal keepContentsMounted>
									<IonDatetime
										min={getNow()}
										id="datetime"
										value={eventDate}
										onIonChange={e => {
											setValues({
												eventDate: e.target.value as string
											});

											setValues(value => {
												if (isDateAfter(value.eventDate?.value, value.eventEndDate?.value)) {
													return {
														eventEndDate: DateTime.fromISO(
															value.eventDate?.value || DateTime.now().toISO()
														)
															.plus({ hours: 1 })
															.toISO()
													};
												}
												return {};
											});
										}}
										minuteValues="0,15,30,45"
										disabled={false}
									></IonDatetime>
								</IonModal>

								<FormErrors errors={formValues.eventDate?.errors} />
							</div>
						)}

						{formValues.eventEndDate?.value && (
							<div
								className={formStyles.fieldContainer}
								style={{
									height: formValues.eventEndDate?.value ? '75px' : '0px',
									marginBottom: formValues.eventEndDate?.value ? '30px' : '0px',
									overflow: 'hidden'
								}}
							>
								<div className={formStyles.label}>Event End Date</div>
								<div className={styles.endDateRemoveField} key={eventEndDate}>
									<IonDatetimeButton
										placeholder="Select an end date"
										data-testid="selectEventEndDate"
										datetime="dateendtime"
										className={classNames(formStyles.input, styles.dateButtonContainer)}
									/>
									<div>
										<IconButton icon={<IconClose />} onClick={handleRemoveEndDate} />
									</div>

									<IonModal keepContentsMounted>
										<IonDatetime
											min={formValues.eventDate?.value}
											id="dateendtime"
											value={eventEndDate}
											onIonChange={e => {
												setValues({
													eventEndDate: e.target.value as string
												});
											}}
											minuteValues="0,15,30,45"
											disabled={false}
										/>
									</IonModal>
								</div>
								<FormErrors errors={formValues.eventEndDate?.errors} />
							</div>
						)}

						{!formValues.eventEndDate?.value && (
							<div className={styles.endTimesButton}>
								<IconButton
									label="Add end date/time"
									icon={<IconAdd />}
									onClick={handleAddEndDate}
									testId="addEndTimeButton"
								/>
							</div>
						)}

						<FormInput
							id="title"
							setValues={setValues}
							field={formValues.title}
							label="Title"
							testId="editPostTitleField"
						/>
						{formValues.topic && (
							<TopicSearchBox
								label="Topic"
								topic={formValues.topic?.value}
								setValues={setValues}
								errors={formValues.topic?.errors}
								testId="editPostTopicField"
							/>
						)}
						<div className={loadingSocial ? styles.fieldFade : ''}>
							<FormInlineImageArea
								setValues={setValues}
								content={formValues?.contents?.value}
								document={event}
							>
								<FormTextarea
									id="contents"
									onPaste={e => onPaste(e)}
									setValues={setValues}
									rows={7}
									onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
										if (loadingSocial) e.preventDefault();
									}}
									field={formValues.contents}
									placeholder="Describe your event, paste images or links to embed them"
									label="Event description"
									testId="editPostContentField"
								/>
								<div
									className={classNames(
										postEditStyles.loading,
										loadingSocial ? postEditStyles.show : ''
									)}
								>
									<div>Fetching Preview</div>
									<IonSpinner style={{ height: '15px' }} />
								</div>
							</FormInlineImageArea>
						</div>

						<FormInput
							nogap={event.images.length < 1}
							id="location"
							setValues={setValues}
							field={formValues.location}
							label="Location"
							testId="editEventLocationField"
						/>

						{event.images.length > 0 && (
							<div className={postStyles.postImageContainer}>
								<BigImageContainer
									post={event}
									edit
									images={filterInlineImages(formValues?.contents?.value as string, event.images)}
								/>
							</div>
						)}

						<LinkPreviewComponent setValues={setValues} linkPreview={formValues.linkPreview?.value} />
					</>
				)}

				<div className={styles.postFooter}>
					<Checkbox onClick={handleCommentCheck} value={event.displayComments} label="Show Comments" />

					{event.images.length < 6 && <UploadImageButton post={event} />}
				</div>
			</div>
		</>
	);
};
